import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { datetimeLocalToUTC, formatMoldovaTime } from '../utils/timezone';

interface PollOption {
  id: string;
  text: string;
  order: number;
  _count?: {
    votes: number;
  };
  votes?: Array<{
    id: string;
    player: {
      id: string;
      name: string;
    } | null;
    chatId: string | null;
    username: string | null;
    createdAt: string;
  }>;
}

interface Poll {
  id: string;
  question: string;
  description: string | null;
  chatId: string;
  messageId: string | null;
  threadId: string | null;
  createdById: string;
  contestId: string | null;
  isActive: boolean;
  allowMultipleAnswers: boolean;
  deadline: string | null;
  hasCorrectResult: boolean;
  correctOptionId: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    id: string;
    name: string;
  };
  contest?: {
    id: string;
    name: string;
  } | null;
  options: PollOption[];
  _count: {
    votes: number;
  };
}

interface TelegramGroup {
  chatId: string;
  title: string | null;
  username: string | null;
  topics?: Array<{
    id: string;
    threadId: string;
    name: string | null;
  }>;
}

interface ContestOption {
  id: string;
  name: string;
  isActive: boolean;
}

const TelegramPolls: React.FC = () => {
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPoll, setEditingPoll] = useState<Poll | null>(null);
  const [editPollData, setEditPollData] = useState({ question: '', description: '' });
  const [selectedPoll, setSelectedPoll] = useState<Poll | null>(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showResultAndPointsModal, setShowResultAndPointsModal] = useState(false);
  const [pointsData, setPointsData] = useState<Array<{ optionId: string; text: string; points: number }>>([]);
  const [resultAndPointsCorrectOptionId, setResultAndPointsCorrectOptionId] = useState('');
  const [savingResultAndPoints, setSavingResultAndPoints] = useState(false);
  const [groups, setGroups] = useState<TelegramGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [contests, setContests] = useState<ContestOption[]>([]);
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  
  const [filters, setFilters] = useState({
    chatId: '',
    isActive: '',
    startDate: '',
    endDate: ''
  });

  const [newPoll, setNewPoll] = useState({
    question: '',
    description: '',
    chatId: '',
    threadId: '',
    contestId: '',
    options: [{ text: '', points: 0 }, { text: '', points: 0 }] as Array<{ text: string; points: number }>,
    allowMultipleAnswers: false,
    deadline: '',
    hasCorrectResult: false,
    correctOptionId: ''
  });

  const fetchGroups = useCallback(async () => {
    try {
      setLoadingGroups(true);
      const groupsData = await apiGet<TelegramGroup[]>('/telegram-group-settings');
      setGroups(groupsData);
    } catch (error) {
      console.error('Ошибка загрузки групп:', error);
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  const fetchContests = useCallback(async () => {
    try {
      const response = await apiGet<{
        contests: ContestOption[];
      }>('/contests?limit=1000');
      const activeContests = (response.contests || []).filter((contest) => contest.isActive);
      setContests(activeContests);
    } catch (error) {
      console.error('Ошибка загрузки конкурсов:', error);
    }
  }, []);

  const fetchPolls = useCallback(async (page = 1, limit = 50) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (filters.chatId) {
        params.append('chatId', filters.chatId);
      }
      if (filters.isActive !== '') {
        params.append('isActive', filters.isActive);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }

      const response = await apiGet<{
        polls: Poll[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>(`/polls?${params.toString()}`);

      setPolls(response.polls);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Ошибка загрузки голосований:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchPolls(pagination.page, pagination.limit);
    fetchGroups();
    fetchContests();
  }, [fetchPolls, pagination.page, pagination.limit, fetchGroups, fetchContests]);

  const handleCreatePoll = async () => {
    try {
      const validOptions = newPoll.options.filter(opt => opt.text.trim() !== '');
      if (validOptions.length < 2) {
        alert('Необходимо указать минимум 2 варианта ответа');
        return;
      }

      if (!newPoll.question.trim()) {
        alert('Необходимо указать вопрос');
        return;
      }

      if (!newPoll.chatId) {
        alert('Необходимо выбрать чат/группу');
        return;
      }

      const poll = await apiPost<Poll>('/polls', {
        question: newPoll.question,
        description: newPoll.description || null,
        chatId: newPoll.chatId,
        threadId: newPoll.threadId || null,
        contestId: newPoll.contestId || null,
        options: validOptions.map(opt => ({
          text: opt.text,
          points: opt.points || 0
        })),
        allowMultipleAnswers: newPoll.allowMultipleAnswers,
        deadline: newPoll.deadline ? datetimeLocalToUTC(newPoll.deadline) : null
      });

      setShowCreateModal(false);
      setNewPoll({
        question: '',
        description: '',
        chatId: '',
        threadId: '',
        contestId: '',
        options: [{ text: '', points: 0 }, { text: '', points: 0 }],
        allowMultipleAnswers: false,
        deadline: '',
        hasCorrectResult: false,
        correctOptionId: ''
      });
      fetchPolls(1, pagination.limit);
    } catch (error: any) {
      console.error('Ошибка создания голосования:', error);
      alert(error.response?.data?.error || 'Ошибка создания голосования');
    }
  };

  const [pollResults, setPollResults] = useState<{
    poll: any;
    results: Array<{
      id: string;
      text: string;
      votes: number;
      percentage: number;
      voters: Array<{
        id: string;
        player: { id: string; name: string } | null;
        chatId: string | null;
        username: string | null;
        createdAt: string;
      }>;
    }>;
  } | null>(null);

  const handleViewResults = async (poll: Poll) => {
    try {
      const results = await apiGet<{
        poll: any;
        results: Array<{
          id: string;
          text: string;
          votes: number;
          percentage: number;
          voters: Array<{
            id: string;
            player: { id: string; name: string } | null;
            chatId: string | null;
            username: string | null;
            createdAt: string;
          }>;
        }>;
      }>(`/polls/${poll.id}/results`);

      setPollResults(results);
      setSelectedPoll(poll);
      setShowResultsModal(true);
    } catch (error) {
      console.error('Ошибка загрузки результатов:', error);
    }
  };

  const handleToggleStatus = async (poll: Poll) => {
    try {
      await apiPut(`/polls/${poll.id}/status`, {
        isActive: !poll.isActive
      });
      fetchPolls(pagination.page, pagination.limit);
    } catch (error: any) {
      console.error('Ошибка обновления статуса:', error);
      alert(error.response?.data?.error || 'Ошибка обновления статуса');
    }
  };

  const handleDelete = async (poll: Poll) => {
    if (!confirm(`Вы уверены, что хотите удалить голосование "${poll.question}"? Это действие нельзя отменить.`)) {
      return;
    }

    try {
      await apiDelete(`/polls/${poll.id}`);
      fetchPolls(pagination.page, pagination.limit);
      alert('Голосование успешно удалено');
    } catch (error: any) {
      console.error('Ошибка удаления голосования:', error);
      alert(error.response?.data?.error || 'Ошибка удаления голосования');
    }
  };

  const handleSetCorrectResult = async (poll: Poll, optionId: string) => {
    try {
      await apiPut(`/polls/${poll.id}/correct-result`, {
        correctOptionId: optionId
      });
      fetchPolls(pagination.page, pagination.limit);
      alert('Правильный результат установлен');
    } catch (error: any) {
      console.error('Ошибка установки правильного результата:', error);
      alert(error.response?.data?.error || 'Ошибка установки правильного результата');
    }
  };

  const handleOpenEditModal = (poll: Poll) => {
    setEditingPoll(poll);
    setEditPollData({
      question: poll.question,
      description: poll.description || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingPoll) return;

    if (!editPollData.question.trim()) {
      alert('Название голосования не может быть пустым');
      return;
    }

    try {
      await apiPut(`/polls/${editingPoll.id}`, {
        question: editPollData.question.trim(),
        description: editPollData.description.trim() || null
      });
      setShowEditModal(false);
      setEditingPoll(null);
      setEditPollData({ question: '', description: '' });
      fetchPolls(pagination.page, pagination.limit);
      alert('Голосование успешно обновлено');
    } catch (error: any) {
      console.error('Ошибка обновления голосования:', error);
      alert(error.response?.data?.error || 'Ошибка обновления голосования');
    }
  };

  const handleOpenPointsModal = (poll: Poll) => {
    setSelectedPoll(poll);
    setPointsData(poll.options.map(opt => ({
      optionId: opt.id,
      text: opt.text,
      points: opt.points || 0
    })));
    setShowPointsModal(true);
  };

  const handleSavePoints = async () => {
    if (!selectedPoll) return;

    try {
      await apiPut(`/polls/${selectedPoll.id}/options/points`, {
        options: pointsData.map(opt => ({
          optionId: opt.optionId,
          points: opt.points
        }))
      });
      setShowPointsModal(false);
      setSelectedPoll(null);
      setPointsData([]);
      fetchPolls(pagination.page, pagination.limit);
      alert('Очки успешно сохранены');
    } catch (error: any) {
      console.error('Ошибка сохранения очков:', error);
      alert(error.response?.data?.error || 'Ошибка сохранения очков');
    }
  };

  const handleOpenResultAndPointsModal = (poll: Poll) => {
    setSelectedPoll(poll);
    setResultAndPointsCorrectOptionId(poll.correctOptionId || '');
    setPointsData(poll.options.map((opt) => ({
      optionId: opt.id,
      text: opt.text,
      points: opt.points || 0
    })));
    setShowResultAndPointsModal(true);
  };

  const handleSaveResultAndPoints = async () => {
    if (!selectedPoll || savingResultAndPoints) return;

    try {
      setSavingResultAndPoints(true);
      await apiPut(`/polls/${selectedPoll.id}/correct-result`, {
        correctOptionId: resultAndPointsCorrectOptionId
      });
      await apiPut(`/polls/${selectedPoll.id}/options/points`, {
        options: pointsData.map((opt) => ({
          optionId: opt.optionId,
          points: opt.points
        }))
      });
      setShowResultAndPointsModal(false);
      setSelectedPoll(null);
      setPointsData([]);
      setResultAndPointsCorrectOptionId('');
      fetchPolls(pagination.page, pagination.limit);
      alert('Результат и очки успешно сохранены');
    } catch (error: any) {
      console.error('Ошибка сохранения результата и очков:', error);
      alert(error.response?.data?.error || 'Ошибка сохранения результата и очков');
    } finally {
      setSavingResultAndPoints(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChatName = (chatId: string): string => {
    const group = groups.find(g => g.chatId === chatId);
    if (group) {
      return group.title || chatId;
    }
    return chatId;
  };

  const selectedGroup = groups.find(g => g.chatId === newPoll.chatId);

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">📊 Голосования</h1>
          <p className="text-gray-600">Создание и управление голосованиями через Telegram</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Создать голосование
        </button>
      </div>

      {/* Фильтры */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chat ID
            </label>
            <input
              type="text"
              value={filters.chatId}
              onChange={(e) => setFilters({ ...filters, chatId: e.target.value })}
              placeholder="Фильтр по Chat ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Статус
            </label>
            <select
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Все</option>
              <option value="true">Активные</option>
              <option value="false">Неактивные</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата начала
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата окончания
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => fetchPolls(1, pagination.limit)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Применить фильтры
            </button>
          </div>
        </div>
      </div>

      {/* Список голосований */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Загрузка голосований...</p>
        </div>
      ) : polls.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 text-lg">Голосования не найдены</p>
        </div>
      ) : (
        <div className="space-y-4">
          {polls.map((poll) => (
            <div key={poll.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {poll.question}
                    </h3>
                    {poll.isActive ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded font-medium">
                        Активно
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded font-medium">
                        Неактивно
                      </span>
                    )}
                    {poll.allowMultipleAnswers && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded font-medium">
                        Множественный выбор
                      </span>
                    )}
                    {poll.contest && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded font-medium">
                        🏆 {poll.contest.name}
                      </span>
                    )}
                  </div>
                  {poll.description && (
                    <p className="text-gray-600 mb-2">{poll.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                    <span className="px-2 py-1 bg-gray-100 rounded">
                      📍 {getChatName(poll.chatId)}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 rounded">
                      👤 {poll.createdBy.name}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 rounded">
                      📅 {formatDate(poll.createdAt)}
                    </span>
                    {poll.deadline && (
                      <span className={`px-2 py-1 rounded ${
                        new Date(poll.deadline) < new Date()
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        ⏰ Дедлайн: {formatMoldovaTime(poll.deadline, 'dd.MM.yyyy HH:mm')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right ml-4">
                  <div className="text-2xl font-bold text-blue-600">
                    {poll._count.votes}
                  </div>
                  <div className="text-sm text-gray-500">голосов</div>
                </div>
              </div>

              {/* Варианты ответов */}
              <div className="space-y-2 mb-4">
                {poll.options.map((option) => {
                  const votesCount = option._count?.votes || 0;
                  const percentage = poll._count.votes > 0
                    ? Math.round((votesCount / poll._count.votes) * 100)
                    : 0;
                  return (
                    <div key={option.id} className="relative">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">
                          {option.text}
                          {poll.correctOptionId === option.id && (
                            <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded font-medium">
                              ✓ Правильный ответ
                            </span>
                          )}
                          {option.points > 0 && (
                            <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded font-medium">
                              ⭐ {option.points} очков
                            </span>
                          )}
                        </span>
                        <span className="text-sm text-gray-600">
                          {votesCount} ({percentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4">
                        <div
                          className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Действия */}
              <div className="flex gap-2 pt-4 border-t border-gray-200 flex-wrap">
                <button
                  onClick={() => handleViewResults(poll)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  📊 Результаты
                </button>
                <button
                  onClick={() => handleOpenEditModal(poll)}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm"
                >
                  ✏️ Редактировать
                </button>
                <button
                  onClick={() => handleToggleStatus(poll)}
                  className={`px-4 py-2 rounded-md text-sm ${
                    poll.isActive
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      : 'bg-green-200 text-green-700 hover:bg-green-300'
                  }`}
                >
                  {poll.isActive ? 'Деактивировать' : 'Активировать'}
                </button>
                <button
                  onClick={() => handleDelete(poll)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                >
                  🗑️ Удалить
                </button>
                {poll.deadline && new Date(poll.deadline) < new Date() && (
                  <div className="flex gap-2 items-center flex-wrap">
                    <div className="flex gap-2 items-center">
                      <span className="text-sm text-gray-600">Правильный результат:</span>
                      <select
                        value={poll.correctOptionId || ''}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleSetCorrectResult(poll, e.target.value);
                          } else {
                            handleSetCorrectResult(poll, '');
                          }
                        }}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Не указан</option>
                        {poll.options.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.text}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={() => handleOpenPointsModal(poll)}
                      className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
                    >
                      ⭐ Указать очки
                    </button>
                    <button
                      onClick={() => handleOpenResultAndPointsModal(poll)}
                      className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
                    >
                      Результат
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Пагинация */}
      {!loading && pagination.pages > 1 && (
        <div className="mt-6 flex justify-center items-center gap-2">
          <button
            onClick={() => {
              const newPage = Math.max(1, pagination.page - 1);
              setPagination({ ...pagination, page: newPage });
            }}
            disabled={pagination.page === 1}
            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Назад
          </button>
          <span className="px-4 py-2 text-sm text-gray-700">
            Страница {pagination.page} из {pagination.pages} (всего: {pagination.total})
          </span>
          <button
            onClick={() => {
              const newPage = Math.min(pagination.pages, pagination.page + 1);
              setPagination({ ...pagination, page: newPage });
            }}
            disabled={pagination.page === pagination.pages}
            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Вперед
          </button>
        </div>
      )}

      {/* Модальное окно создания голосования */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Создать голосование</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Вопрос *
                </label>
                <input
                  type="text"
                  value={newPoll.question}
                  onChange={(e) => setNewPoll({ ...newPoll, question: e.target.value })}
                  placeholder="Введите вопрос голосования"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание (необязательно)
                </label>
                <textarea
                  value={newPoll.description}
                  onChange={(e) => setNewPoll({ ...newPoll, description: e.target.value })}
                  placeholder="Дополнительное описание"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Дедлайн приема голосов (необязательно)
                </label>
                <input
                  type="datetime-local"
                  value={newPoll.deadline}
                  onChange={(e) => setNewPoll({ ...newPoll, deadline: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Чат/Группа *
                </label>
                <select
                  value={newPoll.chatId}
                  onChange={(e) => setNewPoll({ ...newPoll, chatId: e.target.value, threadId: '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Выберите чат/группу</option>
                  {groups.map((group) => (
                    <option key={group.chatId} value={group.chatId}>
                      {group.title || group.chatId}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Конкурс (необязательно)
                </label>
                <select
                  value={newPoll.contestId}
                  onChange={(e) => setNewPoll({ ...newPoll, contestId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Без конкурса</option>
                  {contests.map((contest) => (
                    <option key={contest.id} value={contest.id}>
                      {contest.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedGroup && selectedGroup.topics && selectedGroup.topics.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Топик (необязательно)
                  </label>
                  <select
                    value={newPoll.threadId}
                    onChange={(e) => setNewPoll({ ...newPoll, threadId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Основной поток</option>
                    {selectedGroup.topics.map((topic) => (
                      <option key={topic.id} value={topic.threadId}>
                        {topic.name || `Топик ${topic.threadId}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Варианты ответов * (минимум 2)
                </label>
                {newPoll.options.map((option, index) => (
                  <div key={index} className="mb-2">
                    <input
                      type="text"
                      value={option.text}
                      onChange={(e) => {
                        const newOptions = [...newPoll.options];
                        newOptions[index] = { ...newOptions[index], text: e.target.value };
                        setNewPoll({ ...newPoll, options: newOptions });
                      }}
                      placeholder={`Вариант ${index + 1}`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => {
                      if (newPoll.options.length < 10) {
                        setNewPoll({ ...newPoll, options: [...newPoll.options, { text: '', points: 0 }] });
                      }
                    }}
                    className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    + Добавить вариант
                  </button>
                  {newPoll.options.length > 2 && (
                    <button
                      onClick={() => {
                        const newOptions = newPoll.options.slice(0, -1);
                        setNewPoll({ ...newPoll, options: newOptions });
                      }}
                      className="px-3 py-1 text-sm bg-red-200 text-red-700 rounded hover:bg-red-300"
                    >
                      - Удалить последний
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="allowMultiple"
                  checked={newPoll.allowMultipleAnswers}
                  onChange={(e) => setNewPoll({ ...newPoll, allowMultipleAnswers: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="allowMultiple" className="ml-2 block text-sm text-gray-700">
                  Разрешить множественный выбор
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="hasCorrectResult"
                  checked={newPoll.hasCorrectResult}
                  onChange={(e) => setNewPoll({ ...newPoll, hasCorrectResult: e.target.checked, correctOptionId: e.target.checked ? newPoll.correctOptionId : '' })}
                  disabled={true}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="hasCorrectResult" className="ml-2 block text-sm text-gray-700">
                  Есть правильный результат (можно указать только после дедлайна)
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Правильный результат можно указать только после истечения дедлайна через редактирование голосования
              </p>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewPoll({
                    question: '',
                    description: '',
                    chatId: '',
                    threadId: '',
                    contestId: '',
                    options: [{ text: '', points: 0 }, { text: '', points: 0 }],
                    allowMultipleAnswers: false,
                    deadline: '',
                    hasCorrectResult: false,
                    correctOptionId: ''
                  });
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleCreatePoll}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно результатов */}
      {/* Модальное окно редактирования голосования */}
      {showEditModal && editingPoll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Редактировать голосование</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Название голосования *
                </label>
                <input
                  type="text"
                  value={editPollData.question}
                  onChange={(e) => setEditPollData({ ...editPollData, question: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Введите название голосования"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Описание
                </label>
                <textarea
                  value={editPollData.description}
                  onChange={(e) => setEditPollData({ ...editPollData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  placeholder="Введите описание (необязательно)"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingPoll(null);
                  setEditPollData({ question: '', description: '' });
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {showResultsModal && selectedPoll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Результаты голосования</h2>
            <p className="text-lg font-semibold text-gray-800 mb-2">{selectedPoll.question}</p>
            {selectedPoll.description && (
              <p className="text-gray-600 mb-4">{selectedPoll.description}</p>
            )}

            <div className="space-y-4">
              {pollResults?.results ? (
                pollResults.results.map((result) => (
                  <div key={result.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-900">{result.text}</span>
                      <span className="text-sm text-gray-600">
                        {result.votes} голосов ({result.percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4 mb-3">
                      <div
                        className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                        style={{ width: `${result.percentage}%` }}
                      ></div>
                    </div>
                    {result.voters && result.voters.length > 0 && (
                      <div className="text-sm text-gray-600">
                        <div className="font-medium mb-1">Проголосовали:</div>
                        <div className="space-y-1">
                          {result.voters.map((vote) => (
                            <div key={vote.id}>
                              {vote.player ? (
                                <span>{vote.player.name}</span>
                              ) : (
                                <span>
                                  {vote.chatId} {vote.username ? `(@${vote.username})` : ''}
                                </span>
                              )}
                              <span className="text-gray-400 ml-2">
                                {formatDate(vote.createdAt)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500">Загрузка результатов...</p>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowResultsModal(false);
                  setSelectedPoll(null);
                  setPollResults(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для указания очков */}
      {showPointsModal && selectedPoll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Указать очки для вариантов ответов
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Голосование: <strong>{selectedPoll.question}</strong>
            </p>
            
            <div className="space-y-4">
              {pointsData.map((option, index) => (
                <div key={option.optionId} className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {option.text}
                    </label>
                  </div>
                  <div className="w-32">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={option.points}
                      onChange={(e) => {
                        const normalized = e.target.value.replace(/[^\d]/g, '');
                        const newPointsData = [...pointsData];
                        newPointsData[index] = {
                          ...newPointsData[index],
                          points: parseInt(normalized, 10) || 0
                        };
                        setPointsData(newPointsData);
                      }}
                      onFocus={(e) => e.target.select()}
                      onClick={(e) => e.currentTarget.select()}
                      placeholder="Очки"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowPointsModal(false);
                  setSelectedPoll(null);
                  setPointsData([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleSavePoints}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Сохранить очки
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для результата и очков */}
      {showResultAndPointsModal && selectedPoll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Результат и очки</h2>
            <p className="text-sm text-gray-600 mb-4">
              Голосование: <strong>{selectedPoll.question}</strong>
            </p>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">Правильный результат</label>
              <select
                value={resultAndPointsCorrectOptionId}
                onChange={(e) => setResultAndPointsCorrectOptionId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Не указан</option>
                {selectedPoll.options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-4">
              {pointsData.map((option, index) => (
                <div key={option.optionId} className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {option.text}
                    </label>
                  </div>
                  <div className="w-32">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={option.points}
                      onChange={(e) => {
                        const normalized = e.target.value.replace(/[^\d]/g, '');
                        const newPointsData = [...pointsData];
                        newPointsData[index] = {
                          ...newPointsData[index],
                          points: parseInt(normalized, 10) || 0
                        };
                        setPointsData(newPointsData);
                      }}
                      onFocus={(e) => e.target.select()}
                      onClick={(e) => e.currentTarget.select()}
                      placeholder="Очки"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowResultAndPointsModal(false);
                  setSelectedPoll(null);
                  setPointsData([]);
                  setResultAndPointsCorrectOptionId('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={savingResultAndPoints}
              >
                Отмена
              </button>
              <button
                onClick={handleSaveResultAndPoints}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60"
                disabled={savingResultAndPoints}
              >
                {savingResultAndPoints ? 'Сохранение...' : 'Сохранить всё'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramPolls;
